import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import axios from "axios";
import {
  MessageCircle, Plus, Trash2, Send, Sparkles, Loader2,
  Globe, Eye, Pencil, CheckCircle2, XCircle, Clock,
  MessageSquare, BarChart3
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: "Oczekujacy", bg: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
  approved: { label: "Zatwierdzony", bg: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
  published: { label: "Opublikowany", bg: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" },
  rejected: { label: "Odrzucony", bg: "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400" },
};

export default function Forums() {
  const { t } = useLanguage();
  const [forums, setForums] = useState([]);
  const [forumPosts, setForumPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("forums");
  const [forumDialog, setForumDialog] = useState(false);
  const [postDialog, setPostDialog] = useState(false);
  const [editingForum, setEditingForum] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [publishing, setPublishing] = useState(null);
  const [postFilter, setPostFilter] = useState("all");
  const [forumForm, setForumForm] = useState({ name: "", url: "", forum_type: "mybb", section_id: "", api_key: "", status: "active" });
  const [postForm, setPostForm] = useState({ forum_id: "", title: "", content: "", category: "", tags: "" });

  const fetchData = useCallback(async () => {
    try {
      const [forumsRes, postsRes, statsRes] = await Promise.all([
        axios.get(`${API}/forums`),
        axios.get(`${API}/forums/posts${postFilter !== "all" ? `?status=${postFilter}` : ""}`),
        axios.get(`${API}/forums/stats`),
      ]);
      setForums(forumsRes.data);
      setForumPosts(postsRes.data);
      setStats(statsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [postFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAddForum = async () => {
    try {
      const payload = { ...forumForm, section_id: forumForm.section_id || null, api_key: forumForm.api_key || null };
      if (editingForum) {
        await axios.put(`${API}/forums/${editingForum.id}`, payload);
      } else {
        await axios.post(`${API}/forums`, payload);
      }
      toast.success(t("common.success"));
      fetchData(); setForumDialog(false); setEditingForum(null);
      setForumForm({ name: "", url: "", forum_type: "mybb", section_id: "", api_key: "", status: "active" });
    } catch (err) { toast.error(t("common.error")); }
  };

  const handleEditForum = (f) => {
    setEditingForum(f);
    setForumForm({ name: f.name, url: f.url, forum_type: f.forum_type, section_id: f.section_id || "", api_key: f.api_key || "", status: f.status });
    setForumDialog(true);
  };

  const handleDeleteForum = async (id) => {
    try { await axios.delete(`${API}/forums/${id}`); toast.success(t("common.success")); fetchData(); }
    catch (err) { toast.error(t("common.error")); }
  };

  const handleCreatePost = async () => {
    if (!postForm.forum_id || !postForm.title) return;
    try {
      await axios.post(`${API}/forums/posts`, {
        ...postForm,
        tags: postForm.tags ? postForm.tags.split(",").map(t2 => t2.trim()).filter(Boolean) : []
      });
      toast.success(t("forums.postCreated"));
      fetchData(); setPostDialog(false);
      setPostForm({ forum_id: "", title: "", content: "", category: "", tags: "" });
    } catch (err) { toast.error(t("common.error")); }
  };

  const handleApprove = async (id) => {
    try { await axios.patch(`${API}/forums/posts/${id}`, { status: "approved" }); toast.success(t("common.success")); fetchData(); }
    catch (err) { toast.error(t("common.error")); }
  };

  const handleReject = async (id) => {
    try { await axios.patch(`${API}/forums/posts/${id}`, { status: "rejected" }); toast.success(t("common.success")); fetchData(); }
    catch (err) { toast.error(t("common.error")); }
  };

  const handlePublishPost = async (id) => {
    setPublishing(id);
    try { await axios.post(`${API}/forums/posts/${id}/publish`); toast.success(t("forums.published")); fetchData(); }
    catch (err) { toast.error(t("common.error")); }
    finally { setPublishing(null); }
  };

  const handleDeletePost = async (id) => {
    try { await axios.delete(`${API}/forums/posts/${id}`); if (selectedPost?.id === id) setSelectedPost(null); toast.success(t("common.success")); fetchData(); }
    catch (err) { toast.error(t("common.error")); }
  };

  return (
    <div data-testid="forums-page" className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">{t("forums.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1 font-body">{t("forums.subtitle")}</p>
        </div>
        <Dialog open={postDialog} onOpenChange={setPostDialog}>
          <DialogTrigger asChild>
            <Button data-testid="create-forum-post-btn" className="rounded-lg gap-2" disabled={forums.length === 0}>
              <Sparkles className="w-4 h-4" strokeWidth={1.5} /> {t("forums.createPost")}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle className="font-heading">{t("forums.createPost")}</DialogTitle></DialogHeader>
            <div className="grid gap-3 py-4">
              <div className="space-y-1.5">
                <Label className="text-xs">{t("forums.selectForum")}</Label>
                <Select value={postForm.forum_id} onValueChange={(v) => setPostForm({...postForm, forum_id: v})}>
                  <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {forums.map(f => <SelectItem key={f.id} value={f.id} className="text-xs">{f.name} ({f.forum_type})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("forums.postTitle")}</Label>
                <Input data-testid="forum-post-title" value={postForm.title} onChange={(e) => setPostForm({...postForm, title: e.target.value})} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t("forums.postContent")}</Label>
                <Textarea value={postForm.content} onChange={(e) => setPostForm({...postForm, content: e.target.value})} rows={3} className="resize-none"
                  placeholder={t("forums.contentHint")} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("forums.category")}</Label>
                  <Input value={postForm.category} onChange={(e) => setPostForm({...postForm, category: e.target.value})} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("forums.tags")}</Label>
                  <Input value={postForm.tags} onChange={(e) => setPostForm({...postForm, tags: e.target.value})} placeholder="tag1, tag2..." />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPostDialog(false)}>{t("sites.cancel")}</Button>
              <Button data-testid="save-forum-post-btn" onClick={handleCreatePost}>{t("forums.generateAndSave")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: t("forums.forumsCount"), value: stats.forums, color: "text-slate-500" },
            { label: t("forums.postsCount"), value: stats.posts, color: "text-slate-500" },
            { label: t("forums.pendingApproval"), value: stats.pending, color: "text-amber-500" },
            { label: t("forums.publishedCount"), value: stats.published, color: "text-emerald-500" },
          ].map((s, i) => (
            <Card key={i} className="rounded-xl border shadow-sm bg-card">
              <CardContent className="p-3 text-center">
                <p className={`text-xl font-bold font-heading ${s.color}`}>{s.value}</p>
                <p className="text-[10px] text-muted-foreground">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="forums-tabs">
          <TabsTrigger value="forums" className="gap-1.5">
            <Globe className="w-3.5 h-3.5" strokeWidth={1.5} />
            {t("forums.forumsTab")} <Badge variant="secondary" className="text-[10px] rounded-md ml-1">{forums.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="posts" className="gap-1.5">
            <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.5} />
            {t("forums.postsTab")} <Badge variant="secondary" className="text-[10px] rounded-md ml-1">{forumPosts.length}</Badge>
          </TabsTrigger>
        </TabsList>

        {/* FORUMS TAB */}
        <TabsContent value="forums" className="mt-4">
          <div className="flex justify-end mb-4">
            <Dialog open={forumDialog} onOpenChange={(o) => { setForumDialog(o); if (!o) { setEditingForum(null); setForumForm({ name: "", url: "", forum_type: "mybb", section_id: "", api_key: "", status: "active" }); }}}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="add-forum-btn" className="rounded-lg gap-2">
                  <Plus className="w-4 h-4" strokeWidth={1.5} /> {t("forums.addForum")}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader><DialogTitle className="font-heading">{editingForum ? t("sites.edit") : t("forums.addForum")}</DialogTitle></DialogHeader>
                <div className="grid gap-3 py-4">
                  <Input data-testid="forum-name" placeholder={t("forums.forumName")} value={forumForm.name} onChange={(e) => setForumForm({...forumForm, name: e.target.value})} />
                  <Input placeholder={t("forums.forumUrl")} value={forumForm.url} onChange={(e) => setForumForm({...forumForm, url: e.target.value})} />
                  <div className="grid grid-cols-2 gap-2">
                    <Select value={forumForm.forum_type} onValueChange={(v) => setForumForm({...forumForm, forum_type: v})}>
                      <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mybb" className="text-xs">MyBB</SelectItem>
                        <SelectItem value="phpbb" className="text-xs">phpBB</SelectItem>
                        <SelectItem value="other" className="text-xs">{t("forums.other")}</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input placeholder={t("forums.sectionId")} value={forumForm.section_id} onChange={(e) => setForumForm({...forumForm, section_id: e.target.value})} />
                  </div>
                  <Input placeholder={t("forums.apiKey")} value={forumForm.api_key} onChange={(e) => setForumForm({...forumForm, api_key: e.target.value})} />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setForumDialog(false)}>{t("sites.cancel")}</Button>
                  <Button data-testid="save-forum-btn" onClick={handleAddForum}>{t("sites.save")}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {forums.length === 0 ? (
            <Card className="rounded-xl border shadow-sm bg-card">
              <CardContent className="p-0">
                <div data-testid="no-forums" className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <MessageCircle className="w-10 h-10 mb-3 opacity-20" strokeWidth={1} />
                  <p className="text-xs">{t("forums.noForums")}</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {forums.map((forum) => (
                <Card key={forum.id} data-testid={`forum-${forum.id}`} className="rounded-xl border shadow-sm bg-card hover:shadow-md transition-all">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-foreground">{forum.name}</h3>
                        <p className="text-[10px] text-primary truncate">{forum.url}</p>
                      </div>
                      <div className="flex gap-0.5 shrink-0">
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleEditForum(forum)}>
                          <Pencil className="w-2.5 h-2.5" strokeWidth={1.5} />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => handleDeleteForum(forum.id)}>
                          <Trash2 className="w-2.5 h-2.5" strokeWidth={1.5} />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline" className="text-[9px] rounded-md">{forum.forum_type}</Badge>
                      {forum.section_id && <Badge variant="secondary" className="text-[9px] rounded-md">#{forum.section_id}</Badge>}
                      <Badge className={`text-[9px] border-0 rounded-md ${forum.status === "active" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" : "bg-slate-100 text-slate-500"}`}>
                        {forum.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground">
                      <span>{forum.posts_count || 0} {t("forums.posts")}</span>
                      <span>{forum.discussions_count || 0} {t("forums.discussions")}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* POSTS TAB */}
        <TabsContent value="posts" className="mt-4">
          <div className="flex items-center gap-2 mb-4">
            <Select value={postFilter} onValueChange={setPostFilter}>
              <SelectTrigger data-testid="post-filter" className="h-8 text-xs w-[130px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">{t("queue.all")}</SelectItem>
                <SelectItem value="pending" className="text-xs">{t("forums.pendingApproval")}</SelectItem>
                <SelectItem value="approved" className="text-xs">{t("forums.approved")}</SelectItem>
                <SelectItem value="published" className="text-xs">{t("forums.publishedCount")}</SelectItem>
                <SelectItem value="rejected" className="text-xs">{t("forums.rejected")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className={selectedPost ? "lg:col-span-1" : "lg:col-span-3"}>
              <Card className="rounded-xl border shadow-sm bg-card">
                <CardContent className="p-0">
                  {forumPosts.length === 0 ? (
                    <div data-testid="no-forum-posts" className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                      <MessageCircle className="w-10 h-10 mb-3 opacity-20" strokeWidth={1} />
                      <p className="text-xs">{t("forums.noPosts")}</p>
                    </div>
                  ) : (
                    <ScrollArea className="max-h-[500px]">
                      <div className="divide-y divide-border">
                        {forumPosts.map((post) => {
                          const forum = forums.find(f => f.id === post.forum_id);
                          const st = statusConfig[post.status] || statusConfig.pending;
                          return (
                            <button key={post.id} data-testid={`forum-post-${post.id}`}
                              onClick={() => setSelectedPost(post)}
                              className={`w-full flex items-start gap-3 p-3 text-left transition-all hover:bg-accent/30 ${selectedPost?.id === post.id ? "bg-primary/5" : ""}`}>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-foreground truncate">{post.title}</p>
                                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                  {forum && <Badge variant="outline" className="text-[9px] rounded-md">{forum.name}</Badge>}
                                  <Badge className={`text-[9px] border-0 rounded-md ${st.bg}`}>{st.label}</Badge>
                                  {post.tags?.length > 0 && post.tags.slice(0, 2).map((tag, i) => (
                                    <Badge key={i} variant="secondary" className="text-[8px] rounded-md">{tag}</Badge>
                                  ))}
                                </div>
                              </div>
                              <div className="text-right shrink-0 text-[9px] text-muted-foreground">
                                {post.replies_count > 0 && <div className="flex items-center gap-0.5"><MessageSquare className="w-2.5 h-2.5" />{post.replies_count}</div>}
                                {post.views_count > 0 && <div className="flex items-center gap-0.5"><Eye className="w-2.5 h-2.5" />{post.views_count}</div>}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Detail panel */}
            {selectedPost && (
              <div className="lg:col-span-2">
                <Card data-testid="forum-post-detail" className="rounded-xl border shadow-sm bg-card">
                  <CardHeader className="pb-2 pt-4 px-5">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-sm font-heading text-foreground">{selectedPost.title}</CardTitle>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => handleDeletePost(selectedPost.id)}>
                        <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="px-5 pb-5 pt-2 space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`text-[10px] border-0 rounded-md ${(statusConfig[selectedPost.status] || statusConfig.pending).bg}`}>
                        {(statusConfig[selectedPost.status] || statusConfig.pending).label}
                      </Badge>
                      {selectedPost.category && <Badge variant="outline" className="text-[10px] rounded-md">{selectedPost.category}</Badge>}
                      {selectedPost.tags?.map((tag, i) => <Badge key={i} variant="secondary" className="text-[9px] rounded-md">{tag}</Badge>)}
                    </div>

                    {/* AI Content */}
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider mb-1">{t("forums.aiGenerated")}</p>
                      <p className="text-xs text-foreground/80 leading-relaxed whitespace-pre-wrap">{selectedPost.ai_content}</p>
                    </div>

                    {/* Tracking */}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="p-2 rounded-lg bg-muted/50 text-center">
                        <p className="text-lg font-bold text-foreground">{selectedPost.replies_count || 0}</p>
                        <p className="text-[9px] text-muted-foreground">{t("forums.replies")}</p>
                      </div>
                      <div className="p-2 rounded-lg bg-muted/50 text-center">
                        <p className="text-lg font-bold text-foreground">{selectedPost.views_count || 0}</p>
                        <p className="text-[9px] text-muted-foreground">{t("forums.views")}</p>
                      </div>
                    </div>

                    <Separator />

                    {/* Approval queue actions */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {selectedPost.status === "pending" && (
                        <>
                          <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-emerald-600"
                            data-testid="approve-post" onClick={() => handleApprove(selectedPost.id)}>
                            <CheckCircle2 className="w-3 h-3" /> {t("forums.approve")}
                          </Button>
                          <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-red-500"
                            onClick={() => handleReject(selectedPost.id)}>
                            <XCircle className="w-3 h-3" /> {t("forums.reject")}
                          </Button>
                        </>
                      )}
                      {(selectedPost.status === "approved" || selectedPost.status === "pending") && (
                        <Button size="sm" className="h-7 text-xs gap-1 rounded-md"
                          data-testid="publish-forum-post" onClick={() => handlePublishPost(selectedPost.id)}
                          disabled={publishing === selectedPost.id}>
                          {publishing === selectedPost.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" strokeWidth={1.5} />}
                          {t("forums.publish")}
                        </Button>
                      )}
                    </div>

                    <p className="text-[9px] text-muted-foreground/60">{new Date(selectedPost.created_at).toLocaleString()}</p>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
